import { useState } from 'react';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Clock, CalendarClock } from 'lucide-react';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { useVatLang } from './VatLang';
import type {
    YearBoundary, YearBoundaryEntry, YearDeltaEntry, YearLatePostings,
} from '../../types/vatReporting';

/**
 * The two lists that say whether a year's total is still the year's total.
 *
 * THEY BELONG TOGETHER AND ARE STILL TWO TABLES. The boundary shows what a PERSON moved across
 * December/January; the late postings show what the ERP moved after a month was filed. Same
 * question - "does this year still add up to what was declared?" - two different causes and
 * two different remedies, so mixing the rows would produce a list nobody can act on.
 *
 * BOTH FOLLOW THE TABLE STANDARD (sticky header, sortable, resizable, column filters). They
 * are work lists, not a form pivot - the exemption the matrix and the Erfassungshilfe carry
 * does not apply here.
 *
 * EACH ROW DRILLS INTO ITS OWN MONTH, which is why the server sends `drillYear`/`drillMonth`
 * per row rather than the tab passing the year: a posting carried INTO this year lives in its
 * target month's list, and one carried OUT of it only appears in its origin month with the
 * excluded rows switched on.
 */

const money = (v: number | null) =>
    v === null || v === undefined
        ? '—'
        : new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const periodName = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

export interface BoundaryDrill {
    year: number;
    month: number;
    entryNos: number[];
    includeExcluded: boolean;
    title: string;
    subtitle: string;
}

// ------------------------------------------------------------- the year boundary ----

type BoundaryCol = 'kind' | 'origin' | 'target' | 'entryNo' | 'documentNo'
    | 'counterparty' | 'kennzahl' | 'contribution' | 'comment';

const BOUNDARY_COLUMNS: { key: BoundaryCol; labelKey: string; width: number; align?: 'right' }[] = [
    { key: 'kind', labelKey: 'boundary.colDirection', width: 130 },
    { key: 'origin', labelKey: 'boundary.colOrigin', width: 95 },
    { key: 'target', labelKey: 'boundary.colTarget', width: 95 },
    { key: 'entryNo', labelKey: 'drill.entry', width: 80 },
    { key: 'documentNo', labelKey: 'drill.document', width: 140 },
    { key: 'counterparty', labelKey: 'drill.counterparty', width: 190 },
    { key: 'kennzahl', labelKey: 'ustva.kz', width: 60 },
    { key: 'contribution', labelKey: 'carry.contribution', width: 120, align: 'right' },
    { key: 'comment', labelKey: 'carry.comment', width: 260 },
];

const BOUNDARY_ACCESSOR: Record<BoundaryCol, (r: YearBoundaryEntry) => string | number | null> = {
    kind: r => r.kind,
    origin: r => periodName(r.originYear, r.originMonth),
    target: r => (r.includedInYear !== null && r.includedInMonth !== null
        ? periodName(r.includedInYear, r.includedInMonth) : null),
    entryNo: r => r.entryNo,
    documentNo: r => r.documentNo,
    counterparty: r => r.counterpartyName ?? r.counterpartyNo,
    kennzahl: r => r.kennzahl,
    contribution: r => r.contribution,
    comment: r => r.comment,
};

const BOUNDARY_NUMERIC: BoundaryCol[] = ['entryNo', 'contribution'];

const BOUNDARY_FILTERS = BOUNDARY_COLUMNS.map(c => ({
    key: c.key,
    accessor: BOUNDARY_ACCESSOR[c.key],
    numeric: BOUNDARY_NUMERIC.includes(c.key),
}));

const KIND_ICON = {
    in: <ArrowDownLeft className="h-3.5 w-3.5 text-blue-600" />,
    out: <ArrowUpRight className="h-3.5 w-3.5 text-purple-600" />,
    pending: <Clock className="h-3.5 w-3.5 text-amber-600" />,
};

export function YearBoundaryPanel({ boundary, year, onDrill }: {
    boundary: YearBoundary;
    year: number;
    onDrill: (d: BoundaryDrill) => void;
}) {
    const { t } = useVatLang();
    const [sort, setSort] = useState<SortState<BoundaryCol>>({ key: 'origin', direction: 'asc' });
    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(BOUNDARY_COLUMNS, 'vat-boundary-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(boundary.entries, BOUNDARY_FILTERS);
    const filterLabels = {
        search: t('filter.search'), all: t('filter.all'), none: t('filter.none'),
        from: t('filter.from'), to: t('filter.to'), empty: t('filter.empty'),
    };

    if (boundary.entries.length === 0) return null;

    const onSort = (key: BoundaryCol) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, BOUNDARY_NUMERIC.includes(key)));
    };

    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = BOUNDARY_ACCESSOR[sort.key as BoundaryCol];
            const av = acc(a); const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-gray-500" />
                    {t('boundary.title', { year })}
                </h3>
                <p className="mt-1 text-xs text-gray-600">
                    {t('boundary.summary', {
                        inCount: boundary.entries.filter(e => e.kind === 'in').length,
                        inAmount: money(boundary.inTotal),
                        outCount: boundary.entries.filter(e => e.kind === 'out').length,
                        outAmount: money(boundary.outTotal),
                        pendingCount: boundary.entries.filter(e => e.kind === 'pending').length,
                        pendingAmount: money(boundary.pendingTotal),
                    })}
                </p>
            </div>

            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-gray-50 text-xs">
                    <span>{t('filter.active', { count: activeCount })}</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">
                        {t('filter.clearAll')}
                    </button>
                </div>
            )}

            <div className="overflow-auto" style={{ maxHeight: '40vh' }}>
                <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {BOUNDARY_COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                    </colgroup>
                    <thead>
                        <tr className="border-b">
                            {BOUNDARY_COLUMNS.map(c => (
                                <SortableHeader
                                    key={c.key}
                                    label={t(c.labelKey)}
                                    sortKey={c.key}
                                    sort={sort}
                                    onSort={onSort}
                                    align={c.align}
                                    onResizeStart={x => startResize(c.key, x)}
                                    onResetWidth={() => resetWidth(c.key)}
                                    isResizing={resizingKey === c.key}
                                    filter={
                                        <ColumnFilter
                                            label={t(c.labelKey)}
                                            values={uniqueValuesFor(c.key)}
                                            state={filters[c.key]}
                                            onChange={s => setColumnFilter(c.key, s)}
                                            numeric={BOUNDARY_NUMERIC.includes(c.key)}
                                            active={isFiltered(c.key)}
                                            labels={filterLabels}
                                        />
                                    }
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map(r => (
                            <tr key={r.entryNo} className="hover:bg-gray-50">
                                <td className="px-3 py-1.5 whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1.5">
                                        {KIND_ICON[r.kind]}
                                        <span className="text-xs">{t(`boundary.kind.${r.kind}`)}</span>
                                    </span>
                                </td>
                                <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">
                                    {periodName(r.originYear, r.originMonth)}
                                </td>
                                <td className="px-3 py-1.5 whitespace-nowrap tabular-nums text-gray-600">
                                    {r.includedInYear !== null && r.includedInMonth !== null
                                        ? periodName(r.includedInYear, r.includedInMonth)
                                        : <span className="text-amber-700">{t('boundary.nowhere')}</span>}
                                </td>
                                <td className="px-3 py-1.5 text-gray-500 tabular-nums">
                                    <button className="hover:text-blue-700 hover:underline decoration-dotted"
                                            onClick={() => onDrill({
                                                year: r.drillYear, month: r.drillMonth,
                                                entryNos: [r.entryNo],
                                                includeExcluded: r.drillIncludeExcluded,
                                                title: `${t('boundary.drillTitle')} ${r.entryNo}`,
                                                subtitle: `${periodName(r.drillYear, r.drillMonth)} · ${r.comment}`,
                                            })}>
                                        {r.entryNo}
                                    </button>
                                </td>
                                <td className="px-3 py-1.5 truncate" title={r.documentNo ?? undefined}>
                                    {r.documentNo ?? (
                                        <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                                            <AlertTriangle className="h-3 w-3" />{t('carry.entryMissing')}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 truncate"
                                    title={[r.counterpartyName, r.counterpartyNo].filter(Boolean).join(' · ')}>
                                    {r.counterpartyName ?? <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-3 py-1.5 font-mono">{r.kennzahl ?? '—'}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">{money(r.contribution)}</td>
                                <td className="px-3 py-1.5 text-gray-600 truncate"
                                    title={[r.comment, r.resolutionComment].filter(Boolean).join(' → ')}>
                                    {r.comment}
                                    {r.resolutionComment && (
                                        <span className="text-gray-400"> → {r.resolutionComment}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="px-4 py-2 border-t text-xs text-gray-500">
                {t('boundary.legend')}
            </div>
        </div>
    );
}

// --------------------------------------------------------- postings after filing ----

type LateCol = 'kind' | 'month' | 'entryNo' | 'groups' | 'kennzahl' | 'impact' | 'baseLocal';

const LATE_COLUMNS: { key: LateCol; labelKey: string; width: number; align?: 'right' }[] = [
    { key: 'kind', labelKey: 'late.colKind', width: 110 },
    { key: 'month', labelKey: 'late.colMonth', width: 90 },
    { key: 'entryNo', labelKey: 'drill.entry', width: 80 },
    { key: 'groups', labelKey: 'drill.groups', width: 220 },
    { key: 'kennzahl', labelKey: 'ustva.kz', width: 60 },
    { key: 'impact', labelKey: 'late.colImpact', width: 130, align: 'right' },
    { key: 'baseLocal', labelKey: 'drill.baseLocal', width: 130, align: 'right' },
];

const LATE_ACCESSOR: Record<LateCol, (r: YearDeltaEntry) => string | number | null> = {
    kind: r => r.kind,
    month: r => r.month,
    entryNo: r => r.entryNo,
    groups: r => `${r.vatBusPostingGroup} / ${r.vatProdPostingGroup}`,
    kennzahl: r => r.kennzahl,
    impact: r => r.impact,
    baseLocal: r => r.baseLocal ?? r.snapshotBaseLocal,
};

const LATE_NUMERIC: LateCol[] = ['month', 'entryNo', 'impact', 'baseLocal'];

const LATE_FILTERS = LATE_COLUMNS.map(c => ({
    key: c.key,
    accessor: LATE_ACCESSOR[c.key],
    numeric: LATE_NUMERIC.includes(c.key),
}));

export function LatePostingsPanel({ late, year, monthNames, onDrill }: {
    late: YearLatePostings;
    year: number;
    monthNames: string[];
    onDrill: (d: BoundaryDrill) => void;
}) {
    const { t } = useVatLang();
    const [sort, setSort] = useState<SortState<LateCol>>({ key: 'month', direction: 'asc' });
    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(LATE_COLUMNS, 'vat-late-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(late.entries, LATE_FILTERS);
    const filterLabels = {
        search: t('filter.search'), all: t('filter.all'), none: t('filter.none'),
        from: t('filter.from'), to: t('filter.to'), empty: t('filter.empty'),
    };

    if (!late.available || late.entries.length === 0) return null;

    const onSort = (key: LateCol) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, LATE_NUMERIC.includes(key)));
    };

    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = LATE_ACCESSOR[sort.key as LateCol];
            const av = acc(a); const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    const total = filtered.reduce((s, r) => s + r.impact, 0);

    return (
        <div className="border border-red-200 rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-red-50">
                <h3 className="text-sm font-semibold text-red-900">
                    {t('late.title', { year })}
                </h3>
                <p className="mt-1 text-xs text-red-800">
                    {t('late.summary', {
                        added: late.added, changed: late.changed, removed: late.removed,
                        months: late.monthsAffected.map(m => monthNames[m - 1]).join(', '),
                    })}
                    {late.reconstructedMonths.length > 0 && (
                        <> {t('late.reconstructedNote', {
                            months: late.reconstructedMonths.map(m => monthNames[m - 1]).join(', '),
                        })}</>
                    )}
                    {late.truncated && <> {t('late.truncated')}</>}
                </p>
            </div>

            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-red-50 text-xs">
                    <span>{t('filter.active', { count: activeCount })}</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">
                        {t('filter.clearAll')}
                    </button>
                </div>
            )}

            <div className="overflow-auto" style={{ maxHeight: '40vh' }}>
                <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {LATE_COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                    </colgroup>
                    <thead>
                        <tr className="border-b">
                            {LATE_COLUMNS.map(c => (
                                <SortableHeader
                                    key={c.key}
                                    label={t(c.labelKey)}
                                    sortKey={c.key}
                                    sort={sort}
                                    onSort={onSort}
                                    align={c.align}
                                    onResizeStart={x => startResize(c.key, x)}
                                    onResetWidth={() => resetWidth(c.key)}
                                    isResizing={resizingKey === c.key}
                                    filter={
                                        <ColumnFilter
                                            label={t(c.labelKey)}
                                            values={uniqueValuesFor(c.key)}
                                            state={filters[c.key]}
                                            onChange={s => setColumnFilter(c.key, s)}
                                            numeric={LATE_NUMERIC.includes(c.key)}
                                            active={isFiltered(c.key)}
                                            labels={filterLabels}
                                        />
                                    }
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map(r => (
                            <tr key={`${r.month}-${r.entryNo}-${r.kind}`} className="hover:bg-gray-50">
                                <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                                    {t(`late.kind.${r.kind}`)}
                                </td>
                                <td className="px-3 py-1.5 whitespace-nowrap">
                                    {monthNames[r.month - 1]} {year}
                                </td>
                                <td className="px-3 py-1.5 text-gray-500 tabular-nums">
                                    {/* A `removed` posting is no longer in that month's live list -
                                        that is what removed MEANS - so a drill into the live
                                        projection would open an empty table. It is named as text
                                        with the place it can still be seen: the frozen list on the
                                        Posten tab. Everything else drills. */}
                                    {r.kind === 'removed' ? (
                                        <span title={t('late.removedHint')}>{r.entryNo}</span>
                                    ) : (
                                        <button className="hover:text-blue-700 hover:underline decoration-dotted"
                                                onClick={() => onDrill({
                                                    year, month: r.month, entryNos: [r.entryNo],
                                                    includeExcluded: true,
                                                    title: `${t('late.drillTitle')} ${r.entryNo}`,
                                                    subtitle: `${monthNames[r.month - 1]} ${year} · ${t(`late.kind.${r.kind}`)}`,
                                                })}>
                                            {r.entryNo}
                                        </button>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 truncate text-xs text-gray-600"
                                    title={`${r.vatBusPostingGroup} / ${r.vatProdPostingGroup} · ${r.direction}`}>
                                    {r.vatBusPostingGroup} / {r.vatProdPostingGroup || '(leer)'}
                                </td>
                                <td className="px-3 py-1.5 font-mono">{r.kennzahl ?? '—'}</td>
                                <td className={`px-3 py-1.5 text-right tabular-nums ${
                                    Math.abs(r.impact) >= 0.005 ? 'font-medium text-red-700' : 'text-gray-400'}`}>
                                    {money(r.impact)}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                                    {money(r.baseLocal ?? r.snapshotBaseLocal)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t font-medium">
                        <tr>
                            <td className="px-3 py-2" colSpan={5}>
                                {activeCount > 0
                                    ? t('drill.filteredCount', { shown: filtered.length, total: late.entries.length })
                                    : t('drill.count', { count: late.entries.length })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{money(total)}</td>
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}
